//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { Suspense, useEffect, useRef } from 'react';
import {
  Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from '@tanstack/react-query';

import BrandedTitleBar from '@/components/Elements/BrandedTitleBar';
import { useSettings } from '@/hooks/useSettings';
import { useAuth } from '@/hooks/useAuth';
import { xgtKeys } from '@/services/xgtQueries';
import { getVisibleGroups } from './settingsSections';
import UnsavedChangesProvider from './UnsavedChangesProvider';
import { useUnsavedChanges } from './useUnsavedChanges';

const NullSection = () => null;

const SettingsDialog = () => {
  const { open, selectedLeafKey, scope, selectLeaf, closeSettings } =
    useSettings();
  // A pane switch or a close unmounts the pane, and any edits it holds.
  const { confirmDiscard } = useUnsavedChanges();
  const handleClose = () => confirmDiscard(closeSettings);
  const { userProfile } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = !!userProfile?.userIsAdmin;
  const groups = getVisibleGroups(isAdmin, scope);

  const wasOpen = useRef(false);

  // Refresh server config once on the closed -> open transition. Guarding on
  // the transition prevents a spurious refetch if the modal is re-opened or the
  // leaf is switched while already open. The displayed pane is driven directly
  // by the context's selectedLeafKey, so opening to a specific leaf and
  // in-modal navigation share one source of truth.  Memory is refreshed
  // separately by the Server Status pane's own polling observer.
  useEffect(() => {
    if (open && !wasOpen.current)
      queryClient.invalidateQueries({ queryKey: xgtKeys.config() });
    wasOpen.current = open;
  }, [open, queryClient]);

  if (!open)
    return null;

  // Guard against a stale or hidden leaf key -- an admin-only key for a
  // non-admin, or the default leaf when a scoped entry point does not
  // contain it. Falling back to the first leaf actually on screen rather
  // than to the global default, which for the server and admin scopes is not
  // one of theirs. Resolving from the visible leaves alone is what keeps a
  // scope that shows the user no groups at all (admin, for a non-admin) empty
  // rather than half-populated with a pane its nav does not list.
  const visibleLeaves = groups.flatMap(g => g.leaves);
  const activeLeaf = visibleLeaves.find(l => l.key === selectedLeafKey) ??
    visibleLeaves[0];
  const resolvedKey = activeLeaf?.key;
  const ActiveComponent = activeLeaf?.component ?? NullSection;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 2 }}>
        <BrandedTitleBar
          title="Settings"
          variant="h5"
          actions={(
            <IconButton aria-label="Close settings" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          )}
        />
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', gap: 2,
          height: '70vh', p: 0 }}>
        {/* tabIndex makes these scrollable regions keyboard-accessible: a
            pane (e.g. Server Config) can overflow with no focusable child, so
            without it axe flags "scrollable-region-focusable". */}
        <Box tabIndex={0} sx={{ minWidth: 200, borderRight: 1,
            borderColor: 'divider', overflowY: 'auto', py: 1 }}>
          {groups.map(group => (
            <Box key={group.label} sx={{ mb: 1 }}>
              <Typography variant="overline"
                  sx={{ px: 2, color: 'text.secondary' }}>
                {group.label}
              </Typography>
              <List dense disablePadding>
                {group.leaves.map(leaf => (
                  // ListItem renders the <li>; the button nests inside it so
                  // the <ul> only has <li> children (axe "list" rule).
                  <ListItem key={leaf.key} disablePadding>
                    <ListItemButton
                        selected={leaf.key === resolvedKey}
                        onClick={() => {
                          if (leaf.key !== resolvedKey)
                            confirmDiscard(() => selectLeaf(leaf.key));
                        }}>
                      {/* No icon color override: inherits MUI's default
                          ListItemIcon color (action.active), matching the
                          Settings/Logout icons in the user menu. */}
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <leaf.icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={leaf.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
        <Box tabIndex={0} sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress aria-label="Loading settings" />
            </Box>}>
            <ActiveComponent />
          </Suspense>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const SettingsModal = () => (
  <UnsavedChangesProvider>
    <SettingsDialog />
  </UnsavedChangesProvider>
);

export default SettingsModal;
